import React, { useEffect, useRef, useState } from 'react';
import { HiChevronDown, HiInformationCircle, HiX } from 'react-icons/hi';
import { uploadImage } from '../../../services/assetService';
import {
  buildCreatePayload,
  buildUpdatePayload,
  emptyAchievementForm,
  emptyTriggerFields,
  formFromAchievementDoc,
  HTTP_URL_RE,
  isDeferredAchievement,
  SESSION_TYPE_OPTIONS,
  WEEKDAY_OPTIONS,
} from './achievementFormUtils';
import HinduFestivalMultiSelect from './HinduFestivalMultiSelect';

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-xs text-red-600 whitespace-pre-line">{message}</p> : null;

const inputClass = (error) =>
  `w-full h-10 px-3 text-sm border rounded-xl focus:outline-none bg-white disabled:opacity-50 ${
    error ? 'border-red-300' : 'border-[#ECECEC]'
  }`;

/**
 * Custom Admin dropdown (Sessions-style) — not native system <select>.
 * onChange receives a synthetic event: { target: { value } }.
 */
const FormSelect = ({
  id,
  label,
  required,
  value,
  disabled,
  error,
  onChange,
  placeholder = 'Select…',
  options = [],
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const normalized = options.map((opt) =>
    typeof opt === 'object'
      ? { value: String(opt.value), label: opt.label }
      : { value: String(opt), label: String(opt) }
  );
  const selected = normalized.find((o) => o.value === String(value || ''));
  const display = selected ? selected.label : placeholder;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const pick = (next) => {
    onChange?.({ target: { value: next } });
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="badge-form-dropdown">
      <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <div className="relative">
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          className={`w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border rounded-xl focus:outline-none bg-white truncate disabled:opacity-50 disabled:cursor-not-allowed ${
            error ? 'border-red-300' : 'border-[#ECECEC]'
          } ${!selected ? 'text-gray-400' : 'text-gray-900'}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {display}
        </button>
        <HiChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
        {open ? (
          <div
            className="absolute z-[160] mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200"
            role="listbox"
            aria-labelledby={id}
          >
            {normalized.map((opt) => {
              const isSelected = String(value || '') === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => pick(opt.value)}
                  className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                    isSelected ? 'bg-gray-50 font-semibold' : 'text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <FieldError message={error} />
    </div>
  );
};

/**
 * Structured trigger_config fields per Backend Joi map (no raw JSON).
 */
const TriggerConfigFields = ({ triggerType, trigger, disabled, error, onChange }) => {
  if (!triggerType) {
    return (
      <p className="text-xs text-gray-400">Select a trigger type to configure unlock rules.</p>
    );
  }

  const set = (patch) => onChange({ ...trigger, ...patch });
  const windows = Array.isArray(trigger.windows) ? trigger.windows : [];

  const countField = (label = 'Count (n)') => (
    <div>
      <label htmlFor="trigger-n" className="block text-xs font-medium text-gray-700 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <input
        id="trigger-n"
        type="number"
        min={1}
        value={trigger.n}
        disabled={disabled}
        onChange={(e) => set({ n: e.target.value })}
        className={inputClass(error)}
      />
    </div>
  );

  let body = null;

  switch (triggerType) {
    case 'session_count':
    case 'streak_days':
    case 'mood_before_session_count':
      body = (
        <div className="max-w-xs">
          {countField(triggerType === 'streak_days' ? 'Streak days (n)' : 'Count (n)')}
          {triggerType === 'mood_before_session_count' ? (
            <p className="mt-1 text-[11px] text-amber-700">
              Config is valid; current evaluator may not award mood badges yet.
            </p>
          ) : null}
        </div>
      );
      break;

    case 'weekday_streak':
      body = (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormSelect
            id="trigger-weekday"
            label="Weekday"
            required
            value={trigger.weekday}
            disabled={disabled}
            onChange={(e) => set({ weekday: e.target.value })}
            options={WEEKDAY_OPTIONS}
          />
          {countField('Streak count (n)')}
        </div>
      );
      break;

    case 'single_played_seconds':
    case 'cumulative_played_seconds':
      body = (
        <div className="max-w-xs">
          <label
            htmlFor="trigger-min-seconds"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Min seconds <span className="text-red-500">*</span>
          </label>
          <input
            id="trigger-min-seconds"
            type="number"
            min={1}
            value={trigger.min_seconds}
            disabled={disabled}
            onChange={(e) => set({ min_seconds: e.target.value })}
            className={inputClass(error)}
          />
          <p className="mt-1 text-[11px] text-gray-400">
            {triggerType === 'single_played_seconds'
              ? 'One session must reach this played duration (e.g. 600 = 10 min).'
              : 'Sum of all played seconds must reach this threshold (e.g. 3600 = 1 hour).'}
          </p>
        </div>
      );
      break;

    case 'session_type_first':
      body = (
        <div className="max-w-xs">
          <FormSelect
            id="trigger-session-type"
            label="Session type"
            required
            value={trigger.session_type}
            disabled={disabled}
            onChange={(e) => set({ session_type: e.target.value })}
            options={SESSION_TYPE_OPTIONS}
          />
        </div>
      );
      break;

    case 'session_types_set':
      body = (
        <div className="space-y-3">
          <div>
            <p className="block text-xs font-medium text-gray-700 mb-2">
              Session types <span className="text-red-500">*</span>
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {SESSION_TYPE_OPTIONS.map((t) => {
                const checked = (trigger.types || []).includes(t);
                return (
                  <label
                    key={t}
                    className="inline-flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...new Set([...(trigger.types || []), t])]
                          : (trigger.types || []).filter((x) => x !== t);
                        set({ types: next });
                      }}
                    />
                    {t}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="max-w-xs">{countField('Distinct types needed (n)')}</div>
          <p className="text-[11px] text-gray-400">
            n cannot exceed the number of selected types (badge would never unlock).
          </p>
        </div>
      );
      break;

    case 'special_date':
      body = (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-500">
            Fixed calendar badge (e.g. World Yoga Day). Uses month/day only — not Hindu Festivals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label htmlFor="trigger-month" className="block text-xs font-medium text-gray-700 mb-1">
                Month <span className="text-red-500">*</span>
              </label>
              <input
                id="trigger-month"
                type="number"
                min={1}
                max={12}
                value={trigger.month}
                disabled={disabled}
                onChange={(e) => set({ month: e.target.value })}
                className={inputClass(error)}
              />
            </div>
            <div>
              <label htmlFor="trigger-day" className="block text-xs font-medium text-gray-700 mb-1">
                Day <span className="text-red-500">*</span>
              </label>
              <input
                id="trigger-day"
                type="number"
                min={1}
                max={31}
                value={trigger.day}
                disabled={disabled}
                onChange={(e) => set({ day: e.target.value })}
                className={inputClass(error)}
              />
            </div>
            <div>
              <label htmlFor="trigger-key-opt" className="block text-xs font-medium text-gray-700 mb-1">
                Key <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="trigger-key-opt"
                type="text"
                maxLength={64}
                value={trigger.key}
                disabled={disabled}
                onChange={(e) => set({ key: e.target.value })}
                className={inputClass(false)}
                placeholder="e.g. world_yoga_day"
              />
            </div>
          </div>
        </div>
      );
      break;

    case 'special_date_range':
    case 'special_date_set':
      body = (
        <div className="space-y-3">
          <p className="text-[11px] text-gray-500">
            {triggerType === 'special_date_range'
              ? 'Range badges: choose one multi-day Hindu Festival (start ≠ end). '
              : 'Set badges: choose one single-day Hindu Festival. '}
            Do not use the retired Meditation Special Dates calendar. Eligible dates come
            from that record’s inclusive start→end.
          </p>
          <HinduFestivalMultiSelect
            selectedIds={trigger.hindu_festival_ids || []}
            disabled={disabled}
            dateFilter={triggerType === 'special_date_range' ? 'range' : 'single'}
            onChange={(ids) => set({ hindu_festival_ids: ids })}
          />
        </div>
      );
      break;

    case 'ritual_local_hour_count':
      body = (
        <div className="space-y-3">
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">
              Hour windows <span className="text-red-500">*</span>
            </p>
            {windows.map((w, idx) => (
              <div key={idx} className="flex flex-wrap items-end gap-2">
                <div className="w-24">
                  <label className="block text-[11px] text-gray-500 mb-1">Start (0–23)</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={w.start_hour}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = windows.map((row, i) =>
                        i === idx ? { ...row, start_hour: e.target.value } : row
                      );
                      set({ windows: next });
                    }}
                    className={inputClass(error)}
                    aria-label={`Window ${idx + 1} start hour`}
                  />
                </div>
                <div className="w-24">
                  <label className="block text-[11px] text-gray-500 mb-1">End (0–24)</label>
                  <input
                    type="number"
                    min={0}
                    max={24}
                    value={w.end_hour}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = windows.map((row, i) =>
                        i === idx ? { ...row, end_hour: e.target.value } : row
                      );
                      set({ windows: next });
                    }}
                    className={inputClass(error)}
                    aria-label={`Window ${idx + 1} end hour`}
                  />
                </div>
                {windows.length > 1 ? (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => set({ windows: windows.filter((_, i) => i !== idx) })}
                    className="px-2 py-2 text-xs text-red-600 border border-red-200 rounded-lg disabled:opacity-50"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                set({ windows: [...windows, { start_hour: '0', end_hour: '1' }] })
              }
              className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
            >
              + Add window
            </button>
            <p className="text-[11px] text-gray-400">
              Start ≠ end. Overnight OK (e.g. 21→2). end 24 = hours start…23.
            </p>
          </div>
          <div className="max-w-xs">{countField('Sessions in windows (n)')}</div>
        </div>
      );
      break;

    case 'deferred_program':
      body = (
        <div>
          <label htmlFor="trigger-reason" className="block text-xs font-medium text-gray-700 mb-1">
            Reason <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="trigger-reason"
            type="text"
            maxLength={200}
            value={trigger.reason}
            disabled={disabled}
            onChange={(e) => set({ reason: e.target.value })}
            className={inputClass(false)}
            placeholder="program_locked"
          />
          <p className="mt-1 text-[11px] text-amber-700">
            Deferred program badges stay inactive and are not awarded by the current evaluator.
          </p>
        </div>
      );
      break;

    default:
      body = (
        <p className="text-xs text-amber-700">
          Unknown trigger type “{triggerType}”. Choose a supported type from the list.
        </p>
      );
  }

  return (
    <div className="rounded-xl border border-[#ECECEC] bg-gray-50/60 p-3 space-y-2">
      <p className="text-xs font-medium text-gray-700">Trigger rules</p>
      {body}
      <FieldError message={error} />
    </div>
  );
};

/**
 * Logo: Sessions / Main Categories–style URL ↔ file toggle using shared uploadImage.
 * Mode switches do not clear the current URL. Upload failure keeps the previous value.
 */
const LogoField = ({
  id,
  value,
  error,
  disabled,
  required,
  onChange,
  onClear,
  onUploadingChange,
  resetKey = 0,
}) => {
  const [imageSource, setImageSource] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);
  const trimmed = String(value || '').trim();
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setImageSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setImageSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;
    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadImage(file);
      onChange(uploadedUrl);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Logo {required ? <span className="text-red-500">*</span> : null}
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                imageSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {imageSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste an image URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              imageSource === 'url' ? 'Switch to file upload' : 'Switch to image URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(imageSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {imageSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear ? (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-400">HTTP/HTTPS URL for the badge logo.</p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="image/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear ? (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            ) : null}
          </div>
          {uploading ? (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading image...</span>
            </div>
          ) : null}
          <p className="mt-1 text-xs text-gray-400">
            Image file. Uses the shared Admin upload helper.
          </p>
        </div>
      )}

      {uploadError ? <p className="mt-1 text-xs text-red-600">{uploadError}</p> : null}
      {error && !uploadError ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}

      {canPreview && !previewFailed && !uploading ? (
        <div className="mt-2">
          <img
            src={trimmed}
            alt=""
            className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      ) : null}
    </div>
  );
};

/**
 * Create / edit modal for meditation achievements.
 * Deferred badges: is_active forced off; activation control disabled.
 */
const BadgeFormModal = ({
  open,
  mode, // 'create' | 'edit'
  initialDoc,
  enums,
  busy,
  generalError,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyAchievementForm);
  const [errors, setErrors] = useState({});
  const [logoUploading, setLogoUploading] = useState(false);
  const savingRef = useRef(false);
  const deferred = mode === 'edit' && isDeferredAchievement(initialDoc);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialDoc) {
      setForm(formFromAchievementDoc(initialDoc));
    } else {
      setForm(emptyAchievementForm());
    }
    setErrors({});
    setLogoUploading(false);
    savingRef.current = false;
  }, [open, mode, initialDoc]);

  if (!open) return null;

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handleTriggerTypeChange = (next) => {
    setForm((prev) => ({
      ...prev,
      trigger_type: next,
      deferred: next === 'deferred_program' ? true : prev.deferred,
      is_active: next === 'deferred_program' ? false : prev.is_active,
      // Wipe previous type's fields — Backend .unknown(false) rejects stale keys.
      trigger: emptyTriggerFields(next),
    }));
    setErrors((prev) => {
      const nextErrors = { ...prev, trigger_type: '', trigger: '' };
      return nextErrors;
    });
  };

  const handleSave = async () => {
    if (busy || logoUploading || savingRef.current) return;
    const built =
      mode === 'create'
        ? buildCreatePayload(form)
        : buildUpdatePayload(initialDoc?._id, form, { forceInactive: deferred });
    if (Object.keys(built.errors).length) {
      setErrors(built.errors);
      return;
    }
    setErrors({});
    savingRef.current = true;
    try {
      await onSubmit(built.payload);
    } finally {
      savingRef.current = false;
    }
  };

  const disabled = busy || logoUploading;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-form-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full sm:max-w-2xl max-h-[92vh] overflow-hidden bg-white rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <h2 id="badge-form-title" className="text-base font-semibold text-gray-900">
            {mode === 'create' ? 'Create badge' : 'Edit badge'}
          </h2>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close form"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {deferred ? (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <HiInformationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>
                This badge is <strong>deferred</strong> (program / inactive catalog). Activation
                is blocked by Backend. You may still update logo, copy, and progress visibility.
              </p>
            </div>
          ) : null}

          {generalError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
              {generalError}
            </div>
          ) : null}

          <div>
            <label htmlFor="badge-name" className="block text-xs font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="badge-name"
              type="text"
              value={form.name}
              disabled={disabled}
              onChange={(e) => setField('name', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
            />
            <FieldError message={errors.name} />
          </div>

          <div>
            <label
              htmlFor="badge-sanskrit"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Sanskrit / alternate name <span className="text-red-500">*</span>
            </label>
            <input
              id="badge-sanskrit"
              type="text"
              value={form.sanskrit_name}
              disabled={disabled}
              onChange={(e) => setField('sanskrit_name', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
            />
            <FieldError message={errors.sanskrit_name} />
          </div>

          <div>
            <label
              htmlFor="badge-description"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="badge-description"
              rows={3}
              value={form.description}
              disabled={disabled}
              onChange={(e) => setField('description', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none disabled:opacity-50"
            />
            <FieldError message={errors.description} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormSelect
              id="badge-category"
              label="Category"
              required
              value={form.category}
              disabled={disabled}
              error={errors.category}
              onChange={(e) => setField('category', e.target.value)}
              options={enums.categories || []}
            />
            <FormSelect
              id="badge-rarity"
              label="Rarity / tier"
              required
              value={form.rarity}
              disabled={disabled}
              error={errors.rarity}
              onChange={(e) => setField('rarity', e.target.value)}
              options={enums.rarities || []}
            />
          </div>

          <LogoField
            id="badge-logo-url"
            value={form.logo_url}
            error={errors.logo_url}
            disabled={disabled}
            required={mode === 'create'}
            resetKey={open ? `${mode}-${initialDoc?._id || 'new'}` : 0}
            onUploadingChange={setLogoUploading}
            onChange={(next) => setField('logo_url', next)}
            onClear={() => setField('logo_url', '')}
          />

          <FormSelect
            id="badge-trigger-type"
            label="Trigger type"
            required
            value={form.trigger_type}
            disabled={disabled}
            error={errors.trigger_type}
            onChange={(e) => handleTriggerTypeChange(e.target.value)}
            options={enums.trigger_types || []}
          />

          <TriggerConfigFields
            triggerType={form.trigger_type}
            trigger={form.trigger || emptyTriggerFields(form.trigger_type)}
            disabled={disabled}
            error={errors.trigger}
            onChange={(nextTrigger) => {
              setForm((prev) => ({ ...prev, trigger: nextTrigger }));
              if (errors.trigger) setErrors((prev) => ({ ...prev, trigger: '' }));
            }}
          />

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.show_progress}
                  disabled={disabled}
                  onChange={(e) => setField('show_progress', e.target.checked)}
                />
                Show progress
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={deferred ? false : form.is_active}
                  disabled={disabled || deferred || form.trigger_type === 'deferred_program'}
                  onChange={(e) => setField('is_active', e.target.checked)}
                />
                Active
                {deferred || form.trigger_type === 'deferred_program' ? (
                  <span className="text-xs text-amber-700">(blocked for deferred)</span>
                ) : null}
              </label>
              {mode === 'create' ? (
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.deferred || form.trigger_type === 'deferred_program'}
                    disabled={disabled || form.trigger_type === 'deferred_program'}
                    onChange={(e) => {
                      setField('deferred', e.target.checked);
                      if (e.target.checked) setField('is_active', false);
                    }}
                  />
                  Mark deferred
                </label>
              ) : (
                <p className="text-xs text-gray-500">
                  Deferred flag: {form.deferred ? 'yes' : 'no'}
                </p>
              )}
            </div>
            <FieldError message={errors.is_active} />
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg disabled:opacity-50"
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeFormModal;
